import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Check, Clock } from "lucide-react";
import { useState } from "react";
import { joinEvent } from "@/lib/events";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import { coverFor } from "@/lib/defaultEventImages";
import { CATEGORY_EMOJI } from "@/lib/categories";
import { formatDistance } from "@/lib/geo";

const CATEGORY_ICON = CATEGORY_EMOJI;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function EventCard({ event, index = 0, onJoined }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [joining, setJoining] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);

  const {
    id, title, category, date_time, city, host_name, host_username, host_avatar_url, cover_url,
    requires_approval, capacity, attendees_count = 0,
  } = event;

  const status = localStatus || (event.joined ? "approved" : event.pending ? "pending" : null);
  const atCapacity = capacity != null && attendees_count >= capacity;

  const goToDetail = () => navigate(`/events/${id}`);

  const onJoin = async (e) => {
    e.stopPropagation();
    if (!user) { navigate(`/login?next=/events/${id}`); return; }
    if (status || joining || atCapacity) return;
    setJoining(true);
    try {
      const newStatus = await joinEvent(id, user.id, !!requires_approval);
      setLocalStatus(newStatus);
      toast.success(newStatus === "pending" ? "Request sent." : "You're in.");
      onJoined?.(id);
    } catch (err) {
      toast.error(fmtError(err) || "Could not join.");
    } finally {
      setJoining(false);
    }
  };

  // Right-side action chip on the card body.
  let chip;
  if (status === "approved") {
    chip = { label: <><Check size={12} strokeWidth={3} /> IN</>, klass: "bg-lime text-ink", disabled: true };
  } else if (status === "pending") {
    chip = { label: <><Clock size={12} strokeWidth={2.5} /> PENDING</>, klass: "bg-paper text-ink", disabled: true };
  } else if (atCapacity) {
    chip = { label: "FULL", klass: "bg-paper text-ink/55", disabled: true };
  } else if (joining) {
    chip = { label: "…", klass: "bg-lime text-ink", disabled: true };
  } else if (requires_approval) {
    chip = { label: "REQUEST", klass: "bg-lime text-ink", disabled: false };
  } else {
    chip = { label: "JOIN", klass: "bg-lime text-ink", disabled: false };
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToDetail()}
      data-testid={`event-card-${id}`}
      className="rise cursor-pointer pop overflow-hidden focus:outline-none"
      style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
    >
      {/* Cover */}
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#EDE7D2] border-b-[3px] border-ink">
        <img
          src={coverFor(event)}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />

        {/* Category pill — top-left */}
        <span className="absolute top-3 left-3 inline-flex items-center gap-2 bg-paper border-[2.5px] border-ink rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-ink shadow-pop-sm">
          <span aria-hidden>{CATEGORY_ICON[category] || "✦"}</span>
          {category}
        </span>

        {/* Action chip — top-right */}
        <button
          data-testid={`card-join-${id}`}
          onClick={onJoin}
          disabled={chip.disabled}
          className={`absolute top-3 right-3 inline-flex items-center gap-1.5 border-[2.5px] border-ink rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest shadow-pop-sm transition-transform ${chip.klass} ${chip.disabled ? "" : "active:translate-x-[1px] active:translate-y-[1px]"}`}
        >
          {chip.label}
        </button>

        {/* Capacity badge — bottom-right */}
        <span className="absolute bottom-3 right-3 inline-flex items-center bg-ink text-paper rounded-full px-3 py-1 text-[10px] font-extrabold tracking-widest border-[2.5px] border-ink">
          {attendees_count}{capacity ? ` / ${capacity}` : ""}
        </span>
      </div>

      {/* Body */}
      <div className="p-5 md:p-6 flex flex-col gap-3">
        <h3 className="font-display text-2xl md:text-[1.7rem] font-extrabold leading-tight text-ink">
          {title}
        </h3>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-ink/65">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={14} strokeWidth={2} className="text-ink/55" />
            {formatDate(date_time)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={14} strokeWidth={2} className="text-ink/55" />
            {city}
          </span>
          {Number.isFinite(event.distance_km) && (
            <span data-testid="event-card-distance" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-neon/30 border-[2px] border-ink text-[10px] uppercase tracking-widest font-extrabold">
              {formatDistance(event.distance_km)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Avatar src={host_avatar_url} name={host_name} size="xs" />
          <span className="text-xs text-ink/65 truncate">
            by <span className="text-ink font-bold">{host_username ? `@${host_username}` : (host_name || "anon")}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
